<template>
  <tr>
    <td colspan="7">
      <div class="container" v-if="delivery">
        <div class="row">
          <div class="col-sm-3">
            <h4>{{ _('Delivery method') }}</h4>
            <select class="form-control" v-model="deliverymethod" :disabled="!isEditable">
              <option v-for="deliverymethod in allDeliverymethods" :value="deliverymethod.id">{{ deliverymethod.name }}</option>
            </select>
            <a href="javascript:;" v-on:click="manageDeliverymethodsDialog"> {{ _('Delivery settings') }}</a>
          </div>
          <div class="col-sm-6">
            <h4>&nbsp;</h4>
            <div class="delivery-tags-selector">
              <div class="form-group">
                <v-select :options="allSelectableTags" v-model="tagToAdd" v-on:option:selected="addTag" taggable label="title" keys="['title']" :placeholder="_('Type to search or add tags')">
                  <template v-slot:no-options="{ searching }">
                    <template v-if="searching">
                      {{ _('No results found.') }}
                    </template>
                    <template v-else>{{ _('Type to search or add tags') }}</template>
                  </template>
                </v-select>
              </div>
            </div>
            <div class="delivery-tags-selected">
              <div class="tags-list flex-row" style="margin-top: 5px;">
                <span v-for="tag in tags" class="btn btn-info" v-on:click="removeTag(tag)"><i class="fa fa-fw fa-close"></i> {{ getTagTitle(tag) }}</span>
              </div>
            </div>
          </div>
          <div class="col-sm-3">
            <h4>{{ _('Delivery date') }}</h4>
            <template v-if="isEditable">
              <DatePicker v-model="deliveryDate" :shortLocale="shortLocale" :staticInput="true" :time-picker="true" :time-picker-inline="true" :placeholder="_('Select date')"></DatePicker>
            </template>
            <div class="form-group" v-else-if="delivery.ts && parseInt(delivery.ts, 10) > this.minReasonableTimestamp">
              {{ $filters.timestampToDate(delivery.ts) }} {{ $filters.timestampToTime(delivery.ts) }}
            </div>
            <div class="form-group" v-else>
              {{ _('Immediate') }}
            </div>
          </div>
        </div>
        <hr />
        <div class="row">
          <div class="col-sm-12">
            <h4>{{ _('Products in this delivery') }}</h4>
            <table class="table table-borderless">
              <thead>
                <tr>
                  <th style="width: 40px">&nbsp;</th>
                  <th style="width: 60px">{{ _('ID') }}</th>
                  <th>{{ _('Product') }}</th>
                  <th>{{ _('Count') }}</th>
                  <th>{{ _('Price inc. vat') }}</th>
                </tr>
              </thead>
              <tbody>
                <delivery-item-row v-for="(item, index) in items" :key="index" :item="item" :delivery="delivery" :value="isItemRowSelected(item)" v-on:input="toggleItemRow(item, $event)"></delivery-item-row>
              </tbody>
            </table>
          </div>
        </div>
        <hr />
        <template v-if="delivery.deliverymethod && delivery.deliverymethod.shipit_id">
          <div class="row">
            <div class="col-sm-12">
              <h4>{{ _('Packages in this delivery') }}</h4>
              <table class="table" v-if="packages.length">
                <thead>
                  <tr>
                    <th style="width: 100px">&nbsp;</th>
                    <th>{{ _('Size') }}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(pkg, index) in packages">
                    <td><button class="btn btn-default btn-xs" v-on:click="editPackageDialog(index)" v-if="isEditPackageAvailable"><i class="fa fa-fw fa-pencil"></i></button></td>
                    <td>{{ pkg.height }} {{ _('cm') }} x {{ pkg.width }} {{ _('cm') }} x {{ pkg.depth }} {{ _('cm') }}, {{ pkg.weight }} {{ _('kg') }}</td>
                    <td style="width: 30px"><button class="btn btn-danger btn-xs" v-if="isRemovePackageAvailable"><i class="fa fa-fw fa-trash" v-on:click="removePackage(index)"></i></button></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="well">{{ _('Add packages to create a delivery.') }}</div>
            </div>
          </div>
          <hr />
        </template>
        <div class="row" v-if="hasDeliveryDetails">
          <div class="col-sm-12">
            <h4>{{ _('Delivery details from the order') }}</h4>
            <p v-if="delivery.price">{{ _('Delivery costs') }}: {{ $filters.currency(delivery.price, settings.currency) }}</p>
            <p v-if="delivery.agent_name">{{ _('Pickup location') }}: {{ delivery.agent_name }}</p>
            <p v-if="parseInt(delivery.shipit_status)">{{ _('Shipit') }}: {{ _('Sent') }}</p>
            <p v-if="parseInt(delivery.shipit_orderid)">{{ _('Shipit order') }}: {{ delivery.shipit_orderid }}</p>
            <div class="button-col">
              <button class="btn btn-primary" v-for="(freightdoc, index) in freightDocuments" v-on:click="downloadFreightDoc(freightdoc)"><i class="fa fa-fw fa-file-text"></i> {{ _('Freight document') }}<template v-if="freightDocuments.length > 1"> {{ index + 1 }}</template></button>
              <button class="btn btn-info" v-for="(trackingurl, index) in trackingUrls" v-on:click="openTrackingUrl(trackingurl)"><i class="fa fa-fw fa-map-signs"></i> {{ _('Tracking link') }}<template v-if="trackingUrls.length > 1"> {{ index + 1 }}</template></button>
            </div>
            <hr />
          </div>
        </div>
        <div class="flex-row">
          <button class="btn btn-danger pull-right" v-if="isDeleteAvailable" v-on:click="deleteDelivery"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
          <button v-if="isProcessAvailable" :disabled="!isProcessEnabled" class="btn btn-success" v-on:click="processDelivery(true)"><i class="fa fa-fw fa-cogs"></i> {{ _('Process') }}</button>
          <button v-if="isUnprocessAvailable" :disabled="!isUnprocessEnabled" class="btn btn-default" v-on:click="processDelivery(false)"><i class="fa fa-fw fa-undo"></i> {{ _('Revert to not processed') }}</button>
          <button v-if="isAddPackageAvailable" class="btn btn-success" v-on:click="editPackageDialog()"><i class="fa fa-fw fa-plus"></i> {{ _('Add package') }}</button>
          <button v-if="isChangePickupAvailable" :disabled="!isChangePickupEnabled" class="btn btn-default" v-on:click="changePickupLocationDialog"><i class="fa fa-fw fa-map-marker"></i> <template v-if="loadingPickupLocations">{{ _('Loading...') }}</template><template v-else>{{ _('Change pickup location') }}</template></button>
          <button v-if="isCreateShipmentAvailable" :disabled="!isCreateShipmentEnabled" class="btn btn-primary" v-on:click="createShipmentDialog"><i class="fa fa-fw fa-truck"></i> <template v-if="loadingShippingPrice">{{ _('Loading...') }}</template><template v-else>{{ _('Create shipment') }}</template></button>
          <button v-if="isPrintManifestAvailable" :disabled="!isPrintManifestEnabled" class="btn btn-primary" v-on:click="printManifest"><i class="fa fa-fw fa-print"></i> {{ _('Print delivery manifest') }}</button>
        </div>
        <div class="well" style="margin-top: 10px" v-if="unsavedChanges">{{ _('Save changes to enable action buttons.') }}</div>
        <div class="well" style="margin-top: 10px" v-else-if="!hasContactInfo">{{ _('Add customer details to the order to enable creating shipments.') }}</div>
      </div>
      <table class="table">
      </table>
    </td>
  </tr>
</template>

<script>
import { DateTime } from 'luxon'
import components from '~/lib/components'
import settingshelpers from '~/lib/settingshelpers'
import { DatetimePicker, DeliveryItemRow, DeliverySettings } from '#components'
import { mapGetters } from 'vuex'
import axios from 'axios'

export default {
  data () {
    return {
      minReasonableTimestamp: 1000000000,
      delivery: {},
      tagToAdd: '',
      selectedItems: [],
      loadingPickupLocations: false,
      loadingShippingPrice: false
    }
  },
  mixins: [components, settingshelpers],
  props: ['modelValue', 'allDeliverymethods', 'allSelectedItems', 'allTags', 'order'],
  computed: {
    ...mapGetters(['hostName', 'clientid', 'apiKey', 'pos']),
    isEditable () {
      if (!this.delivery) {
        return true
      }
      if (parseInt(this.delivery.processed)) {
        return false
      }
      return true
    },
    deliveryDate: {
      get () {
        if (!this.delivery || !this.delivery.ts || parseInt(this.delivery.ts, 10) < this.minReasonableTimestamp) {
          return null
        }
        return DateTime.fromSeconds(Number(this.delivery.ts), { zone: this.timezone }).toJSDate()
      },
      set (newValue) {
        if (newValue) {
          this.delivery.ts = DateTime.fromJSDate(newValue, { zone: this.timezone }).toUnixInteger()
        } else {
          this.delivery.ts = null
        }
      }
    },
    deliverymethod: {
      get () {
        return this.delivery ? this.delivery.deliverymethod_id : null
      },
      set (newValue) {
        this.delivery.deliverymethod_id = newValue
        this.delivery.deliverymethod = this.allDeliverymethods.find(dm => parseInt(dm.id, 10) === parseInt(newValue, 10))
      }
    },
    items () {
      if (!this.order || !this.order.ownItem || !this.order.ownItem.length) {
        return []
      }
      return this.order.ownItem.filter(i => i.delivery_id === this.delivery.id)
    },
    tags () {
      return this.delivery && this.delivery.sharedTag ? this.delivery.sharedTag.filter(t => t.title) : []
    },
    allSelectableTags () {
      const ids = this.tags.map(t => t.id)
      return this.allTags.filter(t => !ids.includes(t.id))
    },
    isDeleteAvailable () {
      return this.isEditable && !parseInt(this.delivery.shipit_status)
    },
    isProcessAvailable () {
      return this.delivery && this.delivery.deliverymethod && !parseInt(this.delivery.processed) && parseInt(this.delivery.id) && !parseInt(this.delivery.deliverymethod.shipit_id)
    },
    isUnprocessAvailable () {
      return this.delivery && parseInt(this.delivery.processed) && parseInt(this.delivery.id) && !parseInt(this.delivery.shipit_status)
    },
    isChangePickupAvailable () {
      return this.isCreateShipmentAvailable && this.delivery.deliverymethod && this.delivery.deliverymethod.shipitmethod && this.delivery.deliverymethod.shipitmethod.data && this.delivery.deliverymethod.shipitmethod.data.pickUpPoints
    },
    isChangePickupEnabled () {
      return !this.loadingPickupLocations && this.hasContactInfo
    },
    isAddPackageAvailable () {
      return this.isCreateShipmentAvailable
    },
    isEditPackageAvailable () {
      return this.isAddPackageAvailable
    },
    isRemovePackageAvailable () {
      return this.isAddPackageAvailable
    },
    isCreateShipmentAvailable () {
      if (this.delivery && this.delivery.deliverymethod && this.delivery.deliverymethod.shipit_id && !parseInt(this.delivery.shipit_status)) {
        return true
      }
      return false
    },
    isPrintManifestAvailable () {
      if (!this.isFeatureEnabled('manifestprinter')) {
        return false
      }
      if (this.delivery) {
        return true
      }
      return false
    },
    isPrintManifestEnabled () {
      return !this.unsavedChanges
    },
    isProcessEnabled () {
      return !this.unsavedChanges
    },
    isUnprocessEnabled () {
      return !this.unsavedChanges
    },
    isCreateShipmentEnabled () {
      return !this.unsavedChanges && parseInt(this.delivery.id) && !this.loadingShippingPrice && this.packages.length && this.items.length && this.order.zip
    },
    hasDeliveryDetails () {
      if (this.delivery.price || this.delivery.agent_name || this.delivery.tracking) {
        return true
      }
      // @todo other data? like the selected delivery pickup point
      return false
    },
    freightDocuments () {
      if (!this.delivery || !this.delivery.freightdoc || !JSON.parse(this.delivery.freightdoc)) {
        return []
      }
      return JSON.parse(this.delivery.freightdoc)
    },
    trackingUrls () {
      if (!this.delivery || !this.delivery.trackingurl || !JSON.parse(this.delivery.trackingurl)) {
        return []
      }
      return JSON.parse(this.delivery.trackingurl)
    },
    packages: {
      get () {
        if (this.delivery.packages && JSON.parse(this.delivery.packages)) {
          return JSON.parse(this.delivery.packages)
        }
        if (!this.delivery.deliverymethod) {
          return []
        }
        return [{
          width: this.delivery.deliverymethod.maxwidth,
          height: this.delivery.deliverymethod.maxheight,
          depth: this.delivery.deliverymethod.maxdepth,
          weight: this.delivery.deliverymethod.maxweight,
        }]
      },
      set (newValue) {
        this.delivery.packages = JSON.stringify(newValue)
      }
    },
    hasContactInfo () {
      return this.order && this.order.zip && this.order.address && this.order.city
    }
  },
  methods: {
    downloadFreightDoc (doc) {
      window.open(doc)
    },
    openTrackingUrl (url) {
      window.open(url)
    },
    isItemRowSelected(item) {
      return !!this.selectedItems.find(i => i === item.id)
    },
    toggleItemRow(item, newValue) {
      if (newValue) {
        this.selectedItems.push(item.id)
        this.EventBus.$emit('selectDeliveryItem', item)
      } else {
        this.selectedItems = this.selectedItems.filter(v => v !== item.id)
        this.EventBus.$emit('deselectDeliveryItem', item)
      }
    },
    manageDeliverymethodsDialog () {
      this.showModal('deliverysettings', DeliverySettings, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              modal: true,
              activeTab: 'deliverymethods'
            }
          },
          className: 'modal-content'
        },
        events: {},
        handlers: {},
        width: 1000
      })
    },
    getTagTitle(tag) {
      if (!tag || !tag.title) {
        return ''
      }
      const parts = tag.title.split(':::')
      return parts.pop()
    },
    async addTag (tag) {
      if (!tag) {
        return
      }
      if (typeof tag === 'object') {
        if (tag.id) {
          this.delivery.sharedTag.push({
            id: tag.id,
            title: tag.value
          })
        } else {
          const title = 'delivery:::' + tag.title
          if (!this.delivery.sharedTag.find(t => t.title === title)) {
            this.delivery.sharedTag.push({
              title: title
            })
          }
        }
      } else {
        const title = 'delivery:::' + tag
        if (!this.delivery.sharedTag.find(t => t.title === title)) {
          this.delivery.sharedTag.push({
            title: title
          })
        }
      }
      await nextTick()
      this.tagToAdd = ''
    },
    removeTag(tag) {
      this.delivery.sharedTag = this.delivery.sharedTag.filter(t => t.title !== tag.title)
    },
    deleteDelivery () {
      this.showDialog({
        title: this._('Are you sure? Deletion can\'t be undone.'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.$emit('delete')
            }
          }, 
        ]
      })
    },
    async processDelivery (status) {
      if (!this.delivery || !this.delivery.id) {
        return
      }
      let response
      try {
        const data = {
          id: this.delivery.id,
          processed: status ? 1 : 0
        }
        response = await axios.patch(this.baseDomain + this.shopId + '/deliveries/' + this.delivery.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return
      }
      // Don't set this manually because it will trigger "unsaved changes". This gets updated anyway through the deliveryUpdate event.
      // this.delivery.processed = status ? 1 : 0
    },
    editPackageDialog (index) {
      let pkg
      if (index !== undefined && index !== null) {
        pkg = this.packages[index]
      } else {
        pkg = {}
      }
      this.showDialog({
        title: this._('Package'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['height', 'width', 'depth', 'weight'],
            handler: (data) => {
              this.editPackage(index, data)
            }
          }, 
        ],
        fields: [
          {
            title: this._('Height'),
            type: 'number',
            name: 'height',
            defaultValue: pkg.height,
            addon: this._('cm')
          },
          {
            title: this._('Width'),
            type: 'number',
            name: 'width',
            defaultValue: pkg.width,
            addon: this._('cm')
          },
          {
            title: this._('Depth'),
            type: 'number',
            name: 'depth',
            defaultValue: pkg.depth,
            addon: this._('cm')
          },
          {
            title: this._('Weight'),
            type: 'number',
            name: 'weight',
            defaultValue: pkg.weight,
            addon: this._('kg')
          }
        ]
      })
    },
    editPackage (index, data) {
      if (index === undefined || index === null) {
        this.packages.push(data)
      } else {
        this.packages[index] = data
      }
      this.delivery.packages = JSON.stringify(this.packages)
    },
    removePackage (index) {
      this.packages = this.packages.slice(0, index).concat(this.packages.slice(index + 1, this.packages.length))
    },
    async createShipmentDialog () {
      let response
      this.loadingShippingPrice = true
      try {
        const data = {
          postcode: this.order.zip,
          country: this.order.country,
          deliverymethods: [this.delivery.deliverymethod.id]
        }
        response = await axios.post(this.baseDomain + this.shopId + '/shipithandler/shippingmethods.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.loadingShippingPrice = false
        return this.showError(e)
      }
      const m = response.data.methods[0]
      if (!m) {
        this.loadingShippingPrice = false
        return this.showError(this._('No valid shipping options found.'))
      }
      const curr = this.$filters.currency
      //const shipmentInfo = m.carrier + ', ' + m.serviceName + ': ' + curr(m.price)
      const shipmentInfo = m.carrier + ', ' + m.serviceName
      const returnFreightDocDefault = this.delivery.deliverymethod && this.delivery.deliverymethod.shipit_returnfreightdoc !== null && this.delivery.deliverymethod.shipit_returnfreightdoc !== undefined && this.delivery.deliverymethod.shipit_returnfreightdoc !== ''
        ? String(this.delivery.deliverymethod.shipit_returnfreightdoc)
        : '1'
      this.loadingShippingPrice = false
      this.showDialog({
        title: this._('Create shipment'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['dangerous'],
            handler: (data) => {
              this.createShipment(data)
            }
          }, 
        ],
        fields: [
          {
            type: 'info',
            text: shipmentInfo
          },
          {
            title: this._('This shipment does not contain dangerous items'),
            type: 'checkbox',
            name: 'dangerous'
          },
          {
            title: this._('Include a return freight document'),
            type: 'checkbox',
            name: 'returnFreightDoc',
            defaultValue: returnFreightDocDefault
          }
        ]
      })
    },
    async createShipment (data) {
      let response
      try {
        data.delivery = this.delivery.id
        response = await axios.post(this.baseDomain + this.shopId + '/shipithandler/shipment.json?apikey=' + this.apiKey, data)
      } catch(e) {
        return this.showError(e)
      }
      if (response && response.data) {
        this.showNotification(this._('Shipment created!'))
      }
    },
    async changePickupLocationDialog () {
      let response
      this.loadingPickupLocations = true
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/shipithandler/agents.json?postcode=' + this.order.zip + '&country=' + this.order.country + '&deliverymethod_id=' + this.delivery.deliverymethod.id + '&apikey=' + this.apiKey)
      } catch(e) {
        this.loadingPickupLocations = false
        return this.showError(e)
      }
      this.loadingPickupLocations = false
      if (!response.data || !response.data.locations) {
        return
      }
      const pickupOptions = []
      for (let i = 0; i < response.data.locations.length; i++) {
        const d = response.data.locations[i]
        pickupOptions.push({
          value: d.id,
          name: d.name + ', ' + d.address1
        })
      }
      this.showDialog({
        title: this._('Pickup location'),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: ['agent_id'],
            handler: (data) => {
              this.delivery.agent_id = data.agent_id
              const opt = pickupOptions.find(o => o.value === data.agent_id)
              if (opt) {
                this.delivery.agent_name = opt.name
              }
            }
          }, 
        ],
        fields: [
          {
            title: this._('Pickup location'),
            type: 'select',
            options: pickupOptions,
            name: 'agent_id'
          }
        ]
      })
    },
    async printManifest () {
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/orders/' + this.order.id + '/print.json?print=manifest&deliveries=' + this.delivery.id + '&client=' + this.clientid + '&pos=' + this.pos + '&apikey=' + this.apiKey)
      } catch(e) {
        // We fail silently, yay?
      }
    }
  },
  watch: {
    delivery: {
      deep: true, 
      handler(newValue) {
        this.$emit('update:modelValue', newValue)
      }
    },
    value: {
      handler (newValue) {
        if (JSON.stringify(this.delivery) !== JSON.stringify(newValue)) {
          this.delivery = Object.assign({}, newValue)
        }
      }
    }
  },
  created () {
    this.delivery = Object.assign({}, this.modelValue)
    this.selectedItems = this.items.filter(i => this.allSelectedItems.includes(i.id)).map(i => i.id)
  },
  components: {
    DatetimePicker,
    DeliveryItemRow
  }
}
</script>

<style scoped>
.table-borderless th, .table-borderless td {
  border-width: 0;
}
</style>